import dayjs, { type Dayjs } from 'dayjs';
import {
  lazy,
  Suspense,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { queryTestRuns } from '@/features/executions/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { DetailMetricsStatusChartSection } from '@/features/shared/components/chartPanels/DetailMetricsStatusChartSection';
import { DeferredRenderBlock } from '@/features/shared/components/deferred/DeferredRenderBlock';
import { DeferredTablePanelSkeleton } from '@/features/shared/components/deferred/DeferredSectionSkeletons';
import { DetailDeleteHeaderAction } from '@/features/shared/components/entityDetails/DetailDeleteHeaderAction';
import { DetailHeaderIconButton } from '@/features/shared/components/entityDetails/DetailHeaderIconButton';
import { DetailTablePanel } from '@/features/shared/components/tableSystem/DetailTablePanel';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import {
  collapseStatusOptions,
  expandStatusFilterValues,
} from '@/features/shared/utils/projectStatusPresentation';
import { TestDetailsPanels } from '@/features/tests/components/TestDetailsPanels';
import {
  testRunColumnOptions,
  testRunDefaultColumnVisibilityModel,
  testRunDefaultSortDirection,
  testRunDefaultSortKey,
  testRunSortFieldMap,
  testRunSortOptions,
  testRunStatusOptions,
  type TestRunSortDirection,
  type TestRunSortKey,
} from '@/features/tests/pages/testRunsTableDefinition';
import { testRunSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { detailPageSectionGapToken } from '@/ui/composites/scaffolds/detailPageSpacing';
import { Inline } from '@/ui/layout/Inline';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives/Text';
import type { TestStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { TestDetailPageViewModel } from '@/features/tests/hooks/useTestDetailPageViewModel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { TestDetailDto, TestRunDto, TestRunStatus, QueryResult } from '@flowqa/shared';

type TestDetailContentProps = {
  test: TestDetailDto;
  viewModel: TestDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  onEdit?: () => void;
  deleteLoading?: boolean;
  editLoading?: boolean;
  editDialog?: ReactElement | null;
};

const TestRunsTable = lazy(async () => {
  const module = await import('@/features/tests/components/TestRunsTable');
  return { default: module.TestRunsTable };
});

export const TestDetailContent = ({
  test,
  viewModel,
  onDelete,
  onEdit,
  deleteLoading = false,
  editLoading = false,
  editDialog = null,
}: TestDetailContentProps): ReactElement => {
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const { selectedOrganisationId } = useOrganisationFilter();
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const detailProjectId = test.projectId;
  const [runTableDateQuickFilter, setRunTableDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [runTableFromDate, setRunTableFromDate] = useState<Dayjs | null>(null);
  const [runTableToDate, setRunTableToDate] = useState<Dayjs | null>(null);
  const [debouncedRunTableSearch, setDebouncedRunTableSearch] = useState('');
  const [runTableColumnVisibilityModel, setRunTableColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(testRunDefaultColumnVisibilityModel);
  const [runTableSortKey, setRunTableSortKey] = useState<TestRunSortKey>(testRunDefaultSortKey);
  const [runTableSortDirection, setRunTableSortDirection] = useState<TestRunSortDirection>(
    testRunDefaultSortDirection,
  );
  const [runTableFilterStatuses, setRunTableFilterStatuses] = useState<TestRunStatus[]>([]);
  const [runTableFilterTags, setRunTableFilterTags] = useState<string[]>([]);
  const [runTableMeta, setRunTableMeta] = useState<TableQueryMeta<TestRunDto> | null>(null);

  useEffect(() => {
    const nextQuickFilter: DateQuickFilter =
      viewModel.range === '14d' || viewModel.range === '30d' || viewModel.range === 'latest'
        ? viewModel.range
        : '7d';
    setRunTableDateQuickFilter(nextQuickFilter);
    setRunTableFromDate(viewModel.selectedStartDay ? dayjs(viewModel.selectedStartDay) : null);
    setRunTableToDate(viewModel.selectedEndDay ? dayjs(viewModel.selectedEndDay) : null);
  }, [viewModel.range, viewModel.selectedEndDay, viewModel.selectedStartDay]);

  const runTableDateWindow = useDateWindow({
    dateQuickFilter: runTableDateQuickFilter,
    fromDate: runTableFromDate,
    toDate: runTableToDate,
  });
  const runTableAvailableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const testRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'testRun' });
  const runTableNoRowsText = runTableDateWindow.hasCustomBounds
    ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'testRun' })
    : formatRangeWindowNoDataText(testRunsEmptyState, runTableDateQuickFilter, false);
  const runTableStatusFilterKey = useMemo(
    () => [...runTableFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [runTableFilterStatuses],
  );
  const runTableTagFilterKey = useMemo(
    () => [...runTableFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [runTableFilterTags],
  );
  const effectiveRunTableFilterStatuses = useMemo(
    () => expandStatusFilterValues(runTableFilterStatuses, groupErroredWithFailed),
    [groupErroredWithFailed, runTableFilterStatuses],
  );
  const detailStatusOptions = useMemo(
    () => collapseStatusOptions(testRunSummaryStatusOptions, groupErroredWithFailed),
    [groupErroredWithFailed],
  );
  const runTableStatusOptions = useMemo(
    () => collapseStatusOptions(testRunStatusOptions, groupErroredWithFailed),
    [groupErroredWithFailed],
  );
  const runTableDateBounds = useMemo(
    () => resolveMetaDateBounds(runTableMeta, (item) => item.timestamp),
    [runTableMeta],
  );
  const runTableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestRunDto>> => {
      const isLatestQuickFilter =
        runTableDateQuickFilter === 'latest' &&
        runTableFromDate === null &&
        runTableToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildEqFilterValue('testId', viewModel.testId),
        buildInFilter('status', effectiveRunTableFilterStatuses),
        buildInFilter('tags', runTableFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              runTableDateWindow.startDay
                ? dayjs(runTableDateWindow.startDay).startOf('day').toISOString()
                : null,
              runTableDateWindow.endDay
                ? dayjs(runTableDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: testRunSortFieldMap[runTableSortKey],
        direction: runTableSortDirection,
      };
      return queryTestRuns(detailProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedRunTableSearch,
        sorts: [sortOverride],
        filters: [...baseFilters, ...toolbarFilters],
      });
    },
    [
      debouncedRunTableSearch,
      selectedOrganisationId,
      detailProjectId,
      runTableDateQuickFilter,
      runTableDateWindow.endDay,
      runTableDateWindow.startDay,
      effectiveRunTableFilterStatuses,
      runTableFilterTags,
      runTableFromDate,
      runTableSortDirection,
      runTableSortKey,
      runTableToDate,
      viewModel.testId,
    ],
  );
  const testRunsNoDataText = formatRangeWindowNoDataText(
    testRunsEmptyState,
    viewModel.range,
    hasCustomDates,
  );
  const availableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const handleMetricStatusesChange = useCallback(
    (nextStatuses: TestStatusKey[]): void => {
      viewModel.handleStatusesChange(nextStatuses);
      setRunTableFilterStatuses(nextStatuses);
    },
    [viewModel.handleStatusesChange],
  );
  const metricsToolbarState = useMemo<MetricsStatusChartPanelModelToolbarState<TestStatusKey>>(
    () => ({
      search: '',
      setSearch: () => undefined,
      selectedStatuses: viewModel.selectedStatuses,
      setSelectedStatuses: handleMetricStatusesChange,
      selectedTags: viewModel.selectedTags,
      setSelectedTags: viewModel.handleTagsChange,
      noRowsText: testRunsNoDataText,
      latestQueryStartDay: viewModel.pickerStartDay,
      latestQueryEndDay: viewModel.pickerEndDay,
      dateQuickFilter:
        viewModel.range === '14d' || viewModel.range === '30d' || viewModel.range === 'latest'
          ? viewModel.range
          : '7d',
      onDateQuickFilterChange: (next) => {
        viewModel.setRange(next);
        viewModel.setSelectedStartDay(null);
        viewModel.setSelectedEndDay(null);
      },
      fromDate: viewModel.selectedStartDay ? dayjs(viewModel.selectedStartDay) : null,
      toDate: viewModel.selectedEndDay ? dayjs(viewModel.selectedEndDay) : null,
      onFromDateChange: viewModel.handleStartDayChange,
      onToDateChange: viewModel.handleEndDayChange,
    }),
    [
      testRunsNoDataText,
      viewModel.handleEndDayChange,
      viewModel.handleStartDayChange,
      viewModel.handleTagsChange,
      handleMetricStatusesChange,
      viewModel.pickerEndDay,
      viewModel.pickerStartDay,
      viewModel.range,
      viewModel.selectedEndDay,
      viewModel.selectedStatuses,
      viewModel.selectedStartDay,
      viewModel.selectedTags,
      viewModel.setRange,
      viewModel.setSelectedEndDay,
      viewModel.setSelectedStartDay,
    ],
  );

  return (
    <>
      <DetailPageTemplate
        pageTitle={viewModel.title}
        title={
          <Text role="sectionTitle" component="h1">
            <DetailTitlePrefix label="Tests" href="/tests" />
            {viewModel.title}
          </Text>
        }
        headerActions={
          <Inline gap="md" align="center">
            <DetailHeaderIconButton
              label="Edit Test"
              disabled={editLoading}
              onClick={onEdit ?? (() => undefined)}
            />
            <DetailDeleteHeaderAction
              onDelete={onDelete}
              deleteLoading={deleteLoading}
              dialogTitle={getDialogMessage('destructive.delete.title', { entity: 'test' })}
              dialogDescription={
                <Stack gap="sm">
                  <Text role="body" color="textSecondary">
                    Deleting this test also removes associated test runs.
                  </Text>
                  <Text role="body" color="textSecondary">
                    {getDialogMessage('destructive.cannotBeUndone')}
                  </Text>
                </Stack>
              }
              confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'test' })}
            />
          </Inline>
        }
        metadata={<TestDetailsPanels test={test} metricValueItems={viewModel.metricValueItems} />}
        primaryContent={
          <Section paddingY="none">
            <Stack gap={detailPageSectionGapToken}>
              <DetailMetricsStatusChartSection<TestStatusKey>
                title={getChartTitle('status')}
                searchAriaLabel="Search test run metrics"
                noRowsBaseText={testRunsEmptyState}
                scope="test"
                projectId={test.projectId}
                availableTags={availableTags}
                metricCards={viewModel.testMetricCards}
                metricValueItems={viewModel.metricValueItems}
                loading={viewModel.isChartLoading}
                statusCardSkeletonCount={5}
                toolbarState={metricsToolbarState}
                statusOptions={detailStatusOptions}
                suppressAnimation={viewModel.suppressChartAnimation}
                showPoints={viewModel.showLineChartPoints}
                stackedChartData={viewModel.stackedChartData}
                passRateChartData={viewModel.passRateChartData}
                frequencyChartData={viewModel.frequencyChartData}
                durationChartData={viewModel.durationChartData}
                flakeScoreChartData={viewModel.flakeScoreChartData}
              />
              <DeferredRenderBlock
                fallback={<DeferredTablePanelSkeleton titleWidth={126} variant="testRuns" />}
                minHeight="24rem"
              >
                <DetailTablePanel
                  tabLabel="Runs"
                  tabsAriaLabel="Test detail runs panel"
                  toolbarSlot={
                    <TableToolbar<TestRunSortKey, TestRunStatus>
                      title={undefined}
                      searchAriaLabel="Search test runs"
                      searchValue={debouncedRunTableSearch}
                      onDebouncedSearchChange={setDebouncedRunTableSearch}
                      columnVisibilityModel={runTableColumnVisibilityModel}
                      defaultColumnVisibilityModel={testRunDefaultColumnVisibilityModel}
                      columnOptions={testRunColumnOptions}
                      onColumnVisibilityModelChange={setRunTableColumnVisibilityModel}
                      sortKey={runTableSortKey}
                      sortDirection={runTableSortDirection}
                      defaultSortKey={testRunDefaultSortKey}
                      defaultSortDirection={testRunDefaultSortDirection}
                      sortOptions={testRunSortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setRunTableSortKey(nextSortKey);
                        setRunTableSortDirection(nextSortDirection);
                      }}
                      filterStatuses={runTableFilterStatuses}
                      statusOptions={runTableStatusOptions}
                      onFilterStatusesChange={setRunTableFilterStatuses}
                      filterTags={runTableFilterTags}
                      onFilterTagsChange={setRunTableFilterTags}
                      dateQuickFilter={runTableDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setRunTableDateQuickFilter(next);
                        setRunTableFromDate(null);
                        setRunTableToDate(null);
                      }}
                      fromDate={runTableFromDate}
                      toDate={runTableToDate}
                      onFromDateChange={setRunTableFromDate}
                      onToDateChange={setRunTableToDate}
                      availableTags={runTableAvailableTags}
                      includeLatestQuickFilter
                      statusFilterLabel={getChartTitle('status')}
                      summaryCount={runTableMeta?.total ?? null}
                      summarySingularLabel="test run"
                      summaryPluralLabel="test runs"
                      previewFromDate={
                        runTableDateQuickFilter === 'latest' ? runTableDateBounds.from : null
                      }
                      previewToDate={
                        runTableDateQuickFilter === 'latest' ? runTableDateBounds.to : null
                      }
                    />
                  }
                >
                  <Suspense
                    fallback={<DeferredTablePanelSkeleton titleWidth={126} variant="testRuns" />}
                  >
                    <TestRunsTable
                      persistKey={null}
                      hideNameColumn
                      runColumnFirst
                      tagOptionsOverride={viewModel.tagOptions}
                      queryKey={[
                        'test-detail-executions-table',
                        viewModel.testId,
                        selectedOrganisationId ?? null,
                        detailProjectId,
                        debouncedRunTableSearch,
                        runTableDateQuickFilter,
                        runTableSortKey,
                        runTableSortDirection,
                        runTableStatusFilterKey,
                        runTableTagFilterKey,
                        runTableDateWindow.startDay,
                        runTableDateWindow.endDay,
                      ]}
                      queryFn={runTableQueryFn}
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialPageSize={5}
                      noRowsText={runTableNoRowsText}
                      showToolbar={false}
                      disableColumnSorting
                      disableColumnMenu
                      search={debouncedRunTableSearch}
                      onQueryMeta={setRunTableMeta}
                      columnVisibilityModel={runTableColumnVisibilityModel}
                      onColumnVisibilityModelChange={(nextModel) => {
                        setRunTableColumnVisibilityModel({
                          ...nextModel,
                          name: true,
                        });
                      }}
                    />
                  </Suspense>
                </DetailTablePanel>
              </DeferredRenderBlock>
            </Stack>
          </Section>
        }
      />
      {editDialog}
    </>
  );
};
